import { Fragment, useMemo, useState, type ReactNode } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { EmptyState, HeatGrid } from '@pazportal/ui';
import { Freshness } from '../components/Freshness';
import {
  compareSiteState,
  deriveSiteStatus,
  isException,
} from '../lib/siteStatus';
import { averageWanUptime, formatWanUptime } from '../lib/wanUptime';
import { useCustomersInScope } from '../hooks/useCustomer';
import { useLocations } from '../hooks/useLocations';
import { OPEN_TICKET_STATUSES, useTickets } from '../hooks/useTickets';
import {
  DEVICE_POLL_EXPECTED_REFRESH_MS,
  DEVICE_POLL_STALE_MS,
  useOpenNetworkAlerts,
} from '../hooks/useLocationDevices';
import { useFieldTrips } from '../hooks/useFieldTrips';
import {
  HEALTH_EXPECTED_REFRESH_MS,
  HEALTH_STALE_MS,
  useCustomerHealthScores,
} from '../hooks/useCustomerHealthScore';
import { useActivityFeed } from '../hooks/useActivityFeed';
import {
  aggregateUnifi,
  unifiByLocation,
  UNIFI_EXPECTED_REFRESH_MS,
  UNIFI_STALE_MS,
  useUnifiSites,
} from '../hooks/useUnifiSites';
import { useAccount } from '../contexts/AccountContext';
import { SkeletonCard, SkeletonRow } from '../components/Skeleton';
import { DashboardLayoutMenu } from '../components/DashboardLayoutMenu';
import { BrandLogo } from '../components/BrandLogo';
import { useUserPreferences, type DashboardWidget } from '../hooks/useUserPreferences';
import { StatusBadge } from '../components/StatusBadge';
import { ActiveFieldProjectCard } from '../components/ActiveFieldProjectCard';
import { useI18n } from '../i18n/I18nProvider';
import type { MessageKey } from '../i18n/messages';
import { formatSolutionType, formatDate, timeAgo, daysBetween, formatDuration } from '../lib/formatters';
import { OnsiteTripCalendar } from '../components/OnsiteTripCalendar';
import { PortfolioTripCalendar } from '../components/PortfolioTripCalendar';
import { DeployedSolutionList, type SolutionTone } from '../components/PropertyTripList';
import { usePropertyCatalog } from '../hooks/usePropertyCatalog';
import {
  codesWithVisits,
  listDeployedSolutions,
  visitHistoryNote,
  visitsForProperty,
  type SolutionType,
} from '../lib/propertyCatalog';

type Tone = 'green' | 'amber' | 'red' | 'default';
const TONE_CLASS: Record<Tone, string> = {
  green: 'text-success',
  amber: 'text-warning',
  red: 'text-notification',
  default: 'text-text',
};

function MetricCard({
  label,
  value,
  subtitle,
  tone = 'default',
  to,
  extra,
}: {
  label: string;
  value: string | number;
  subtitle?: string;
  tone?: Tone;
  to?: string;
  extra?: ReactNode;
}) {
  const body = (
    <>
      <p className="text-sm font-medium text-text-muted">{label}</p>
      <p className={`mt-1 text-xl font-semibold sm:text-2xl ${TONE_CLASS[tone]}`}>{value}</p>
      {subtitle && <p className="mt-1 text-xs text-text-faint">{subtitle}</p>}
      {extra && <div className="mt-2">{extra}</div>}
    </>
  );
  const className =
    'block rounded-lg border border-border bg-surface p-4 text-left transition-colors hover:bg-surface-strong focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary sm:p-5';
  return to ? (
    <Link to={to} className={className}>
      {body}
    </Link>
  ) : (
    <div className={className}>{body}</div>
  );
}

const ACTOR_KEY: Record<string, MessageKey> = {
  system: 'dashboard.actor.system',
  paznetworks: 'dashboard.actor.paznetworks',
  customer: 'dashboard.actor.customer',
};

export function DashboardPage() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const { visibleWidgets: widgetOrder } = useUserPreferences();
  const { accounts, activeAccount, isPortfolio, ready } = useAccount();
  const locationsQuery = useLocations();
  const openQuery = useTickets({ status: OPEN_TICKET_STATUSES });
  const { data: allTickets } = useTickets();
  const { data: fieldTrips } = useFieldTrips();
  const { data: healthScores } = useCustomerHealthScores();
  const activity = useActivityFeed({ limit: 10 });
  const { data: alertData } = useOpenNetworkAlerts();
  const { data: unifiSites } = useUnifiSites();
  const { data: customers } = useCustomersInScope();
  const [dismissed, setDismissed] = useState<Set<string>>(new Set());

  const locations = useMemo(() => locationsQuery.data?.locations ?? [], [locationsQuery.data]);
  // Memoised: it feeds the portfolio grid's useMemo, and a fresh [] each render
  // would rebuild every cell on every render.
  const openTickets = useMemo(() => openQuery.data ?? [], [openQuery.data]);
  const unifi = useMemo(() => aggregateUnifi(unifiSites ?? []), [unifiSites]);
  const unifiLocations = useMemo(() => unifiByLocation(unifiSites ?? []), [unifiSites]);

  // PazNetworks' own record of what it deployed and when it was on site, now
  // read from property_directory and property_visit_history, keyed by the
  // registry link on the account's locations. A site with no link shows no
  // catalog: the fix is an alias in the registry, not a guess from its name.
  const { catalog } = usePropertyCatalog();
  const catalogCode = useMemo(() => {
    if (isPortfolio || !activeAccount) return null;
    return locations.map((l) => l.directory?.code).find((code): code is string => !!code) ?? null;
  }, [isPortfolio, activeAccount, locations]);
  const catalogVisits = useMemo(() => visitsForProperty(catalog, catalogCode), [catalog, catalogCode]);
  const catalogNote = useMemo(() => visitHistoryNote(catalog, catalogCode), [catalog, catalogCode]);
  // Portfolio mode: only the in-scope properties that have a recorded visit.
  const scopedCatalogCodes = useMemo(() => {
    const linked = new Set(
      locations.map((l) => l.directory?.code).filter((code): code is string => !!code),
    );
    return codesWithVisits(catalog).filter((code) => linked.has(code));
  }, [catalog, locations]);
  const calendarLocations = useMemo(() => locations.map((l) => ({ id: l.id, name: l.name })), [locations]);

  // Contract renewal: the soonest date across the scoped accounts.
  const nextRenewal = useMemo(() => {
    const today = new Date().toISOString();
    return (customers ?? [])
      .filter((c) => c.contract_renewal_date)
      .map((c) => ({
        name: c.name,
        date: c.contract_renewal_date!,
        days: daysBetween(today, c.contract_renewal_date!),
      }))
      .sort((a, b) => a.days - b.days)[0] ?? null;
  }, [customers]);
  const renewalTone: Tone =
    nextRenewal === null ? 'default' : nextRenewal.days < 30 ? 'red' : nextRenewal.days < 90 ? 'amber' : 'green';

  // Deployments are nearly empty in production; only render when rows exist.
  const deployments = locations.flatMap((loc) => (loc.deployments ?? []).map((d) => ({ ...d, location: loc })));
  const offlineDeployments = deployments.filter((d) => d.status === 'offline' && !dismissed.has(d.id));
  const solutionStatuses = [...new Set(deployments.map((d) => d.solution_type))].map((type) => {
    const rows = deployments.filter((d) => d.solution_type === type);
    return {
      type,
      tone: rows.some((d) => d.status === 'offline')
        ? 'red'
        : rows.some((d) => d.status === 'degraded')
          ? 'amber'
          : 'green',
    };
  });
  // Deployed solutions from `deployments` rows, with a health dot per solution.
  const deployedSolutions = listDeployedSolutions(deployments.map((d) => d.solution_type));
  const solutionTone = Object.fromEntries(solutionStatuses.map((s) => [s.type, s.tone])) as Partial<Record<SolutionType, SolutionTone>>;

  // Portfolio command view: one cell per site, stated from the signals that are
  // actually present for it. A site with no telemetry is "unknown" rather than
  // healthy — an unmonitored site must never read as a green one.
  const siteCells = useMemo(() => {
    const openByLocation = new Map<string, number>();
    for (const ticket of openTickets) {
      if (!ticket.location_id) continue;
      openByLocation.set(ticket.location_id, (openByLocation.get(ticket.location_id) ?? 0) + 1);
    }

    return locations
      .map((location) => {
        const unifiSite = unifiLocations.get(location.id) ?? null;
        const openCount = openByLocation.get(location.id) ?? 0;
        const { state, telemetryState, telemetryAgeMs } = deriveSiteStatus(
          { telemetry: unifiSite },
          UNIFI_STALE_MS,
        );
        const uptime = formatWanUptime(unifiSite?.wanUptimePct);

        // The figure on a live site is WAN uptime. Colour stays live/down.
        // A site that stopped reporting is not the same as one never instrumented.
        const detail =
          state === 'critical'
            ? t('dashboard.sites_down')
            : uptime
              ? t('dashboard.sites_uptime', { pct: uptime })
            : state === 'good'
              ? t('dashboard.sites_live')
              : telemetryState === 'stale'
                ? t('dashboard.sites_telemetry_lost', {
                    age: formatDuration(telemetryAgeMs ?? 0),
                  })
                : t('dashboard.sites_no_telemetry');

        return {
          id: location.id,
          // A site with no registry code shows its name, not three letters of
          // it: "HIL" reads like a property code the customer does not have.
          label: location.directory?.code ?? location.name,
          title: location.name,
          brand: location.directory?.brand ?? null,
          state,
          detail,
          openCount,
          onSelect: () => navigate(`/locations/${location.id}`),
        };
      })
      .sort((a, b) => compareSiteState(a.state, b.state) || a.title.localeCompare(b.title));
  }, [locations, navigate, openTickets, t, unifiLocations]);

  // The exception rail: networks that are down, worst first.
  const exceptions = useMemo(
    () => siteCells.filter((cell) => isException(cell.state)).slice(0, 6),
    [siteCells],
  );
  const networks = useMemo(() => {
    let live = 0;
    let down = 0;
    for (const cell of siteCells) {
      if (cell.state === 'good') live += 1;
      else if (cell.state === 'critical') down += 1;
    }
    const wanUptimePct = averageWanUptime(
      locations.map((location) => unifiLocations.get(location.id)?.wanUptimePct),
    );
    return { live, down, monitored: live + down, wanUptimePct };
  }, [siteCells, locations, unifiLocations]);

  // SLA compliance: only tickets that carry a deadline, and only once the
  // deadline has been resolved against (closed, or already passed).
  const sla = useMemo(() => {
    const now = Date.now();
    const evaluated = (allTickets ?? []).filter(
      (tk) => tk.sla_deadline && (tk.resolved_at || new Date(tk.sla_deadline).getTime() < now),
    );
    const met = evaluated.filter((tk) => tk.resolved_at && tk.sla_deadline && tk.resolved_at <= tk.sla_deadline);
    return {
      total: evaluated.length,
      met: met.length,
      pct: evaluated.length > 0 ? Math.round((met.length / evaluated.length) * 100) : null,
    };
  }, [allTickets]);

  // Operational health: latest row per scored account, averaged in portfolio mode.
  const health = useMemo(() => {
    const rows = (healthScores ?? []).filter((h) => h.operational_score !== null);
    if (rows.length === 0) return null;
    const score = Math.round(rows.reduce((sum, h) => sum + (h.operational_score ?? 0), 0) / rows.length);
    const latest = rows.reduce<string | null>(
      (max, h) => (!max || h.computed_at > max ? h.computed_at : max),
      null,
    );
    const avg = (key: 'tickets_signal' | 'sla_signal' | 'uptime_signal') => {
      const values = rows.map((h) => h[key]).filter((v): v is number => v !== null);
      return values.length ? values.reduce((a, b) => a + b, 0) / values.length : null;
    };
    return {
      score,
      scored: rows.length,
      computedAt: latest,
      signals: [
        { key: 'dashboard.uptime' as MessageKey, value: avg('uptime_signal') },
        { key: 'dashboard.tickets' as MessageKey, value: avg('tickets_signal') },
        { key: 'dashboard.sla' as MessageKey, value: avg('sla_signal') },
      ],
    };
  }, [healthScores]);

  const activeTrip =
    fieldTrips?.find((trip) => trip.status === 'in_progress') ?? fieldTrips?.find((trip) => trip.status === 'scheduled');

  const isLoading = !ready || locationsQuery.isLoading || openQuery.isLoading;

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <SkeletonCard key={i} />
          ))}
        </div>
        <div className="rounded-lg border border-border bg-surface p-6">
          {Array.from({ length: 5 }).map((_, i) => (
            <SkeletonRow key={i} />
          ))}
        </div>
      </div>
    );
  }

  if (accounts.length === 0) {
    return (
      <div className="rounded-lg border border-border bg-surface">
        <EmptyState icon={<BuildingIcon />} title={t('account.none')} subtitle={t('account.none_hint')} />
      </div>
    );
  }

  const loadError = locationsQuery.error ?? openQuery.error;
  const alertsConnected = alertData?.connected ?? false;
  const openAlerts = alertData?.alerts ?? [];
  const alertsLastPolledAt = alertData?.lastPolledAt ?? null;

  // Keyed by DashboardWidget in both directions: adding a widget to the list
  // without a node here fails to compile, and removing one leaves a key with no
  // type to match. Neither can drift into a section that silently vanishes.
  const widgetNodes: Record<DashboardWidget, ReactNode> = {
    metrics: (
      <>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 sm:gap-4 lg:grid-cols-4">
        <MetricCard
          label={t('dashboard.locations')}
          value={locations.length}
          subtitle={t('dashboard.locations_mapped', { n: unifiLocations.size })}
          to="/locations"
        />
        <MetricCard
          label={t('dashboard.network')}
          value={
            formatWanUptime(networks.wanUptimePct)
              ?? (networks.monitored > 0
                ? t('dashboard.network_sites_live', { live: networks.live, total: networks.monitored })
                : t('common.none'))
          }
          tone={networks.down > 0 ? 'red' : networks.monitored > 0 ? 'green' : 'default'}
          subtitle={
            networks.monitored > 0
              ? networks.down > 0
                ? t('dashboard.network_sites_down', { n: networks.down })
                : t('dashboard.network_all_live')
              : t('dashboard.network_none')
          }
          extra={
            unifi.siteCount > 0 ? (
              <Freshness
                lastUpdatedAt={unifi.latestUpdatedAt}
                label={t('dashboard.network_source')}
                expectedRefreshMs={UNIFI_EXPECTED_REFRESH_MS}
                staleMs={UNIFI_STALE_MS}
                // A quarter-width tile cannot hold the full wording; it wrapped
                // and the rounded-full chip drew an ellipse over its own text.
                compact
              />
            ) : (
              <p className="text-xs text-text-faint">{t('dashboard.network_none_hint')}</p>
            )
          }
          to="/locations"
        />
        <MetricCard
          label={t('dashboard.open_tickets')}
          value={openTickets.length}
          subtitle={t('dashboard.open_tickets_hint')}
          tone={openTickets.length > 3 ? 'red' : openTickets.length > 0 ? 'amber' : 'green'}
          to="/tickets"
        />
        <MetricCard
          label={t('dashboard.renewal')}
          value={
            nextRenewal
              ? nextRenewal.days < 0
                ? t('dashboard.renewal_past')
                : t('dashboard.renewal_days', { n: nextRenewal.days })
              : t('common.none')
          }
          subtitle={
            nextRenewal
              ? isPortfolio
                ? t('dashboard.renewal_account', { name: nextRenewal.name, date: formatDate(nextRenewal.date) })
                : formatDate(nextRenewal.date)
              : t('dashboard.renewal_none')
          }
          tone={renewalTone}
          to="/billing"
        />
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 sm:gap-4 lg:grid-cols-4">
        <MetricCard
          label={t('dashboard.alerts')}
          value={alertData === undefined ? t('common.none') : alertsConnected ? openAlerts.length : t('dashboard.alerts_not_connected')}
          subtitle={alertsConnected ? (openAlerts.length > 0 ? t('dashboard.alerts_open') : t('dashboard.alerts_none')) : undefined}
          tone={alertsConnected && openAlerts.length > 0 ? 'amber' : 'default'}
          to={openAlerts[0]?.location_id ? `/locations/${openAlerts[0].location_id}` : '/locations'}
        />
      </div>
      </>
    ),
    sites: (
      <>
      {siteCells.length > 1 && (
        <section className="rounded-lg border border-border bg-surface p-4 sm:p-5" aria-labelledby="sites-heading">
          <div className="mb-3 flex flex-col gap-1 sm:flex-row sm:items-baseline sm:justify-between">
            <div>
              <h2 id="sites-heading" className="text-sm font-medium text-text-muted">
                {t('dashboard.sites_title')}
              </h2>
              <p className="mt-0.5 text-xs text-text-faint">{t('dashboard.sites_subtitle')}</p>
            </div>
            {/* The grid states each site from UniFi counts, so it carries the
                same freshness contract as every other live surface. */}
            <Freshness
              lastUpdatedAt={unifi.latestUpdatedAt}
              label={t('dashboard.network_source')}
              expectedRefreshMs={UNIFI_EXPECTED_REFRESH_MS}
              staleMs={UNIFI_STALE_MS}
            />
          </div>
          <HeatGrid
            cells={siteCells}
            legendTitle={t('dashboard.sites_legend')}
            stateLabels={{
              good: t('dashboard.sites_state_good'),
              warning: t('dashboard.sites_state_warning'),
              serious: t('dashboard.sites_state_serious'),
              critical: t('dashboard.sites_state_critical'),
              unknown: t('dashboard.sites_state_unknown'),
            }}
          />

          <div className="mt-4 border-t border-divider pt-3">
            <h3 className="mb-2 text-xs font-semibold uppercase tracking-[0.1em] text-text-muted">
              {t('dashboard.exceptions_title')}
            </h3>
            {exceptions.length === 0 ? (
              <p className="text-sm text-text-faint">{t('dashboard.exceptions_none')}</p>
            ) : (
              <ul className="divide-y divide-divider">
                {exceptions.map((cell) => (
                  <li key={cell.id}>
                    <Link
                      to={`/locations/${cell.id}`}
                      className="flex min-h-11 items-center justify-between gap-3 py-2 hover:bg-surface-strong"
                    >
                      <span className="flex min-w-0 items-center gap-3">
                        <BrandLogo brand={cell.brand} name={cell.title} className="hidden h-7 flex-shrink-0 sm:inline-flex" />
                        <span className="min-w-0">
                          <span className="block truncate text-sm text-text">{cell.title}</span>
                          <span className="block truncate text-xs text-text-muted">{cell.detail}</span>
                        </span>
                      </span>
                      <span className="flex shrink-0 items-center gap-2">
                        {cell.openCount > 0 && (
                          <span className="text-xs text-text-muted">
                            {t('dashboard.exceptions_tickets', { n: cell.openCount })}
                          </span>
                        )}
                        <StatusBadge status="offline" label={t('dashboard.sites_down')} />
                      </span>
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </section>
      )}
      </>
    ),
    alerts: (
      <>
      {alertsConnected && openAlerts.length > 0 && (
        <section className="rounded-lg border border-border bg-surface p-4 sm:p-5" aria-labelledby="alerts-heading">
          <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
            <h3 id="alerts-heading" className="text-sm font-medium text-text-muted">{t('dashboard.alerts_open')}</h3>
            {/* An empty or unchanging alert list means nothing unless the poll
                that fills it is alive, so the list carries its heartbeat. */}
            <Freshness
              lastUpdatedAt={alertsLastPolledAt}
              label={t('dashboard.alerts')}
              expectedRefreshMs={DEVICE_POLL_EXPECTED_REFRESH_MS}
              staleMs={DEVICE_POLL_STALE_MS}
            />
          </div>
          <ul className="divide-y divide-divider">
            {openAlerts.slice(0, 8).map((alert, i) => (
              <li key={alert.id ?? `alert-${i}`}>
                <Link
                  to={alert.location_id ? `/locations/${alert.location_id}` : '/locations'}
                  className="flex min-h-11 items-center justify-between gap-3 py-2 hover:bg-surface-strong"
                >
                  <div className="min-w-0">
                    <p className="truncate text-sm text-text">{alert.alert_name ?? alert.entity_name ?? t('dashboard.alerts')}</p>
                    {alert.entity_name && alert.alert_name && (
                      <p className="truncate text-xs text-text-muted">{alert.entity_name}</p>
                    )}
                  </div>
                  {alert.severity && <span className="text-xs capitalize text-text-muted">{alert.severity}</span>}
                </Link>
              </li>
            ))}
          </ul>
        </section>
      )}
      </>
    ),
    systems: (
      <>
      {deployedSolutions.length > 0 && (
        <section className="rounded-lg border border-border bg-surface p-4 sm:p-5" aria-labelledby="systems-heading">
          <h3 id="systems-heading" className="mb-3 text-sm font-medium text-text-muted">
            {t('property.solutions.title')} ({deployedSolutions.length})
          </h3>
          <DeployedSolutionList solutions={deployedSolutions} status={solutionTone} />
        </section>
      )}
      </>
    ),
    health: (
      <>
      <div className="grid grid-cols-1 gap-4 sm:gap-6 lg:grid-cols-2">
        <section className="rounded-lg border border-border bg-surface p-4 sm:p-5" aria-labelledby="health-heading">
          <div className="mb-4 flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
            <div>
              <h3 id="health-heading" className="text-sm font-medium text-text-muted">{t('dashboard.operational_health')}</h3>
              <p className="mt-0.5 text-xs text-text-faint">{t('dashboard.operational_health_subtitle')}</p>
            </div>
            {health && (
              <div className="text-left sm:text-right">
                <p
                  className={`text-2xl font-semibold ${
                    health.score >= 75 ? 'text-success' : health.score >= 50 ? 'text-warning' : 'text-notification'
                  }`}
                >
                  {health.score}
                </p>
                <p className="text-xs text-text-faint">{t('dashboard.health_of_100')}</p>
              </div>
            )}
          </div>
          {health ? (
            <>
              <div className="grid grid-cols-3 gap-3">
                {health.signals.map((sig) => {
                  const pct = sig.value !== null ? Math.round((sig.value / 20) * 100) : null;
                  return (
                    <div key={sig.key}>
                      <p className="mb-1 text-xs text-text-muted">{t(sig.key)}</p>
                      {pct === null ? (
                        <p className="text-xs text-text-faint">{t('common.none')}</p>
                      ) : (
                        <div
                          className="h-2 w-full overflow-hidden rounded-full bg-surface-3"
                          role="meter"
                          aria-valuemin={0}
                          aria-valuemax={100}
                          aria-valuenow={pct}
                          aria-label={t(sig.key)}
                        >
                          <div
                            className={`h-full ${pct >= 75 ? 'bg-success' : pct >= 50 ? 'bg-warning' : 'bg-notification'}`}
                            style={{ width: `${pct}%` }}
                          />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
              <div className="mt-3 flex flex-wrap items-center gap-2">
                <Freshness
                  lastUpdatedAt={health.computedAt}
                  label={t('dashboard.operational_health')}
                  expectedRefreshMs={HEALTH_EXPECTED_REFRESH_MS}
                  staleMs={HEALTH_STALE_MS}
                />
                {isPortfolio && (
                  <span className="text-xs text-text-faint">
                    {t('dashboard.health_accounts_scored', { n: health.scored, total: accounts.length })}
                  </span>
                )}
              </div>
            </>
          ) : (
            <div>
              <p className="text-sm font-medium text-text">{t('dashboard.health_not_enough')}</p>
              <p className="mt-1 text-xs text-text-faint">{t('dashboard.health_not_enough_hint')}</p>
            </div>
          )}
        </section>

        <section className="rounded-lg border border-border bg-surface p-4 sm:p-5" aria-labelledby="sla-heading">
          <h3 id="sla-heading" className="mb-1 text-sm font-medium text-text-muted">{t('dashboard.sla_compliance')}</h3>
          {sla.pct === null ? (
            <>
              <p className="mt-2 text-xl font-semibold text-text sm:text-2xl">{t('common.none')}</p>
              <p className="mt-1 text-xs text-text-faint">{t('dashboard.sla_none')}</p>
            </>
          ) : (
            <>
              <p className="mb-3 text-xs text-text-faint">{t('dashboard.sla_met', { met: sla.met, total: sla.total })}</p>
              <div
                className="mb-1 h-3 w-full rounded-full bg-surface-3"
                role="meter"
                aria-valuemin={0}
                aria-valuemax={100}
                aria-valuenow={sla.pct}
                aria-label={t('dashboard.sla_compliance')}
              >
                <div
                  className={`h-3 rounded-full ${sla.pct >= 99 ? 'bg-success' : sla.pct >= 95 ? 'bg-warning' : 'bg-notification'}`}
                  style={{ width: `${Math.min(sla.pct, 100)}%` }}
                />
              </div>
              <p className="text-right text-sm font-medium text-text">{sla.pct}%</p>
            </>
          )}
        </section>
      </div>
      </>
    ),
    trips: (
      <>
      {activeTrip && <ActiveFieldProjectCard trip={activeTrip} />}

      {ready && !isPortfolio && activeAccount && (
        <OnsiteTripCalendar
          key={activeAccount.customer_id}
          customerId={activeAccount.customer_id}
          visits={catalogVisits}
          visitNote={catalogNote}
          liveTrips={fieldTrips ?? []}
          locations={calendarLocations}
        />
      )}
      {ready && isPortfolio && scopedCatalogCodes.length > 0 && <PortfolioTripCalendar propertyCodes={scopedCatalogCodes} />}
      </>
    ),
    activity: (
      <>
      <section className="rounded-lg border border-border bg-surface p-4 sm:p-5" aria-labelledby="activity-heading">
        <h3 id="activity-heading" className="mb-3 text-sm font-medium text-text-muted">{t('dashboard.recent_activity')}</h3>
        {activity.isLoading ? (
          Array.from({ length: 5 }).map((_, i) => <SkeletonRow key={i} />)
        ) : activity.isError ? (
          <p role="alert" className="text-sm text-notification">
            {t('dashboard.activity_error')} {activity.error instanceof Error ? activity.error.message : ''}
          </p>
        ) : (activity.data?.events.length ?? 0) === 0 ? (
          <EmptyState icon={<ClockIcon />} title={t('dashboard.activity_empty')} subtitle={t('dashboard.activity_empty_hint')} />
        ) : (
          <ul className="divide-y divide-divider">
            {activity.data!.events.map((event) => {
              const href = event.location_id
                ? `/locations/${event.location_id}`
                : event.entity_type === 'tickets'
                  ? `/tickets/${event.entity_id}`
                  : null;
              const row = (
                <>
                  <div className="flex min-w-0 items-center gap-3">
                    <span aria-hidden="true" className="flex h-2 w-2 flex-shrink-0 rounded-full bg-primary" />
                    <span className="min-w-0">
                      <span className="block truncate text-sm text-text">{event.summary}</span>
                      <span className="block text-xs text-text-faint">{t(ACTOR_KEY[event.actor_kind] ?? 'dashboard.actor.system')}</span>
                    </span>
                  </div>
                  <span className="flex-shrink-0 text-xs text-text-faint">{timeAgo(event.occurred_at)}</span>
                </>
              );
              return (
                <li key={event.id}>
                  {href ? (
                    <Link to={href} className="flex min-h-11 items-center justify-between gap-3 py-2 hover:bg-surface-strong">
                      {row}
                    </Link>
                  ) : (
                    <div className="flex min-h-11 items-center justify-between gap-3 py-2">{row}</div>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </section>
      </>
    ),
  };

  return (
    <div className="space-y-4 sm:space-y-6">
      {loadError && (
        <div role="alert" className="rounded-lg border border-notification bg-notification-soft px-4 py-3 text-sm text-notification">
          {t('dashboard.error')} {loadError instanceof Error ? loadError.message : ''}
        </div>
      )}

      {offlineDeployments.map((d) => (
        <div
          key={d.id}
          className="flex items-start justify-between gap-3 rounded-lg border border-notification bg-notification-soft px-3 py-3 sm:items-center sm:px-4"
        >
          <Link to={`/locations/${d.location.id}`} className="flex min-w-0 items-start gap-2 sm:items-center">
            <span aria-hidden="true" className="mt-1.5 flex h-2 w-2 flex-shrink-0 rounded-full bg-notification sm:mt-0" />
            <span className="break-words text-sm font-medium text-notification">
              {d.location.name} — {formatSolutionType(d.solution_type)} {t('dashboard.offline')}
              {d.last_health_check && ` (${t('dashboard.offline_since')} ${timeAgo(d.last_health_check)})`}
            </span>
          </Link>
          <button
            type="button"
            onClick={() => setDismissed((prev) => new Set([...prev, d.id]))}
            className="min-h-11 flex-shrink-0 px-2 text-sm text-notification hover:opacity-80 sm:min-h-6"
          >
            {t('dashboard.dismiss')}
          </button>
        </div>
      ))}

      <div className="flex flex-wrap items-center justify-between gap-2">
        <h1 className="text-lg font-semibold text-text">{t('nav.dashboard')}</h1>
        <DashboardLayoutMenu />
      </div>

      {widgetOrder.map((widget) => (
        <Fragment key={widget}>{widgetNodes[widget]}</Fragment>
      ))}
    </div>
  );
}

function BuildingIcon() {
  return (
    <svg className="h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
    </svg>
  );
}

function ClockIcon() {
  return (
    <svg className="h-10 w-10" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  );
}
